import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, CornerDownLeft, ExternalLink } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { construirSecciones, esVisible } from './Sidebar';
import { TEMAS } from '../pages/Reportes';
import { AYUDA_PANTALLAS } from '../ayuda/pantallas';
import { normalizar } from '../ayuda/markdown';
import { formatMonto, formatFecha } from '../utils/format';

// Buscador general de la barra superior: pantallas, reportes y acciones (del
// mismo menú del Sidebar y del índice de Reportes, con la misma visibilidad
// por rol, sección y módulo) más los datos de la empresa activa que trae
// GET /api/buscar. Se abre con Ctrl K / ⌘ K o con la lupa en el celular.

const ACCIONES = [
  { label: 'Nuevo movimiento bancario', to: '/movimientos?accion=nuevo', rol: 'operador', seccion: 'movimientos' },
  { label: 'Transferencia entre cuentas', to: '/movimientos?accion=transferencia', rol: 'operador', seccion: 'movimientos' },
  { label: 'Cargar tasa de cambio', to: '/tasas-cambio', rol: 'operador', seccion: 'tasas_cambio' },
  { label: 'Registrar cobranza', to: '/clientes', rol: 'operador', modulo: 'ventas' },
  { label: 'Nueva factura de compra', to: '/proveedores', rol: 'operador', modulo: 'proveedores' },
  { label: 'Abrir o cerrar turno de caja', to: '/caja', rol: 'operador', modulo: 'caja' }
];

const ESTADO_FACTURA = { pendiente: 'pendiente', parcial: 'pago parcial', pagada: 'pagada', anulada: 'anulada' };
const DOCUMENTO = { factura: 'Factura', nota_credito: 'Nota de crédito', nota_debito: 'Nota de débito' };

// Resalta la primera coincidencia del texto buscado, sin importar acentos.
function Resaltado({ texto, q }) {
  const t = String(texto || '');
  const i = q ? normalizar(t).indexOf(q) : -1;
  if (i < 0) return t;
  return (
    <>
      {t.slice(0, i)}
      <mark className="rounded-sm bg-amber-200/70 text-inherit">{t.slice(i, i + q.length)}</mark>
      {t.slice(i + q.length)}
    </>
  );
}

function simbolo(moneda) {
  return moneda === 'BS' || moneda === 'VES' ? 'Bs ' : '$';
}

export default function BuscadorGeneral() {
  const navigate = useNavigate();
  const { empresaActiva, empresaActivaNombre, empresaActivaData, rolActual, esAdminGlobal, seccionesDeshabilitadas, modulosActivos } = useAuth();
  const rol = esAdminGlobal ? 'admin' : rolActual || 'consultor';
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [datos, setDatos] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [activo, setActivo] = useState(0);
  const inputRef = useRef(null);
  const listaRef = useRef(null);
  const q = normalizar(texto.trim());

  // Ctrl K / ⌘ K abre o cierra desde cualquier pantalla.
  useEffect(() => {
    function alTeclear(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAbierto((a) => !a);
      }
    }
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, []);

  useEffect(() => {
    if (abierto) setTimeout(() => inputRef.current?.focus(), 0);
    else { setTexto(''); setDatos(null); }
  }, [abierto]);

  // Datos de la empresa activa, con una pausa corta mientras se escribe.
  useEffect(() => {
    const limpio = texto.trim();
    if (!abierto || limpio.length < 2 || !empresaActiva) { setDatos(null); setBuscando(false); return undefined; }
    let vigente = true;
    setBuscando(true);
    const id = setTimeout(() => {
      api.get('/buscar', { params: { q: limpio } })
        .then(({ data }) => { if (vigente) setDatos(data); })
        .catch(() => { if (vigente) setDatos(null); })
        .finally(() => { if (vigente) setBuscando(false); });
    }, 250);
    return () => { vigente = false; clearTimeout(id); };
  }, [texto, abierto, empresaActiva]);

  // Pantallas, reportes y acciones que el usuario puede abrir.
  const funciones = useMemo(() => {
    const visible = (item) => esVisible(item, rol, seccionesDeshabilitadas, modulosActivos);
    const lista = [];
    const vistos = new Set();
    const agregar = (label, to, donde) => {
      if (!to || vistos.has(`${label}|${to}`)) return;
      vistos.add(`${label}|${to}`);
      lista.push({ label, to, donde });
    };
    construirSecciones(empresaActivaData?.rubro).forEach((s) => s.items.forEach((item) => {
      if (!visible(item)) return;
      agregar(item.label, item.to, s.titulo || 'Menú');
      (item.hijos || []).filter(visible).forEach((h) => agregar(h.label, h.to, item.label));
    }));
    TEMAS.forEach((t) => t.reportes.filter((x) => !x.pronto && visible(x.req)).forEach((x) => agregar(x.label, x.to, `Reportes · ${t.titulo}`)));
    ACCIONES.filter(visible).forEach((a) => agregar(a.label, a.to, 'Acción'));
    return lista;
  }, [rol, seccionesDeshabilitadas, modulosActivos, empresaActivaData?.rubro]);

  const manual = useMemo(() => {
    const vistos = new Set();
    return Object.values(AYUDA_PANTALLAS).filter((p) => {
      if (!p.seccion || vistos.has(p.seccion)) return false;
      vistos.add(p.seccion);
      return true;
    });
  }, []);

  // Grupos de resultados en el orden en que se muestran; cada fila sabe
  // adónde lleva.
  const grupos = useMemo(() => {
    if (q.length < 1) return [];
    const coincide = (t) => normalizar(t).includes(q);
    const primero = (a, b) => Number(normalizar(b.titulo).startsWith(q)) - Number(normalizar(a.titulo).startsWith(q));
    const salida = [];
    const fn = funciones.filter((f) => coincide(f.label) || coincide(f.donde))
      .map((f) => ({ clave: `f-${f.label}-${f.to}`, titulo: f.label, detalle: f.donde, ir: () => navigate(f.to) }))
      .sort(primero).slice(0, 6);
    if (fn.length) salida.push({ titulo: 'Funciones y reportes', filas: fn });

    if (datos) {
      const cli = datos.clientes.map((c) => ({
        clave: `c-${c.id}`, titulo: c.nombre, detalle: c.rif || 'Cliente',
        ir: () => navigate('/clientes', { state: { clienteId: c.id } })
      }));
      if (cli.length) salida.push({ titulo: 'Clientes', filas: cli });
      const prov = datos.proveedores.map((p) => ({
        clave: `p-${p.id}`, titulo: p.nombre, detalle: p.rif || 'Proveedor',
        ir: () => navigate('/proveedores', { state: { proveedorId: p.id } })
      }));
      if (prov.length) salida.push({ titulo: 'Proveedores', filas: prov });
      const fac = datos.facturas.map((f) => ({
        clave: `fa-${f.id}`,
        titulo: `${f.tipo === 'compra' ? 'Compra' : 'Venta'} · ${DOCUMENTO[f.tipo_documento] || 'Factura'} N° ${f.numero || 's/n'} · ${f.parte_nombre || ''}`,
        detalle: `${formatFecha(f.fecha)} · ${simbolo(f.moneda)}${formatMonto(f.total)} · ${ESTADO_FACTURA[f.estado] || f.estado}`,
        ir: () => (f.tipo === 'compra'
          ? navigate('/proveedores', { state: { proveedorId: f.proveedor_id } })
          : navigate('/clientes', { state: { clienteId: f.cliente_id } }))
      }));
      if (fac.length) salida.push({ titulo: 'Facturas', filas: fac });
      const prod = datos.productos.map((p) => ({
        clave: `pr-${p.id}`, titulo: p.nombre, detalle: p.unidad_medida ? `Producto · ${p.unidad_medida}` : 'Producto',
        ir: () => navigate(`/productos?q=${encodeURIComponent(p.nombre)}`)
      }));
      if (prod.length) salida.push({ titulo: 'Productos', filas: prod });
      const mov = datos.movimientos.map((m) => ({
        clave: `m-${m.id}`,
        titulo: `${m.referencia ? `Ref. ${m.referencia} · ` : ''}${m.descripcion || (m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso')}`,
        detalle: `${formatFecha(m.fecha)} · ${m.cuenta_nombre} · ${m.tipo === 'egreso' ? '−' : ''}${simbolo(m.cuenta_moneda)}${formatMonto(m.monto)}`,
        ir: () => navigate(`/movimientos?cuenta_id=${m.cuenta_id}&q=${encodeURIComponent(m.referencia || texto.trim())}`)
      }));
      if (mov.length) salida.push({ titulo: 'Movimientos', filas: mov });
    }

    const ayuda = manual.filter((p) => coincide(p.titulo) || coincide(p.resumen)).slice(0, 3).map((p) => ({
      clave: `a-${p.seccion}`, titulo: `Manual › ${p.titulo}`, detalle: 'Abre en pestaña nueva', externo: true,
      ir: () => window.open(`/ayuda#${p.seccion}`, '_blank', 'noopener')
    }));
    if (ayuda.length) salida.push({ titulo: 'Ayuda', filas: ayuda });
    return salida;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, funciones, manual, datos]);

  const filas = grupos.flatMap((g) => g.filas);
  useEffect(() => { setActivo(0); }, [q, datos]);
  useEffect(() => {
    listaRef.current?.querySelector(`[data-indice="${activo}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activo]);

  function abrir(fila) {
    setAbierto(false);
    fila.ir();
  }

  function alTeclearEnInput(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActivo((i) => Math.min(i + 1, filas.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && filas[activo]) { e.preventDefault(); abrir(filas[activo]); }
    else if (e.key === 'Escape') { e.preventDefault(); setAbierto(false); }
  }

  const esMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
  let indice = -1;

  return (
    <>
      {/* Escritorio: campo en la barra; celular: solo la lupa. */}
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="hidden lg:flex w-56 xl:w-72 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-400 hover:border-slate-300 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-turquoise-500/40"
      >
        <Search size={15} />
        <span className="flex-1 text-left">Buscar…</span>
        <kbd className="rounded border border-slate-200 bg-white px-1.5 font-sans text-[11px] text-slate-400">{esMac ? '⌘' : 'Ctrl'} K</kbd>
      </button>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        title="Buscar"
        className="lg:hidden flex-shrink-0 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-turquoise-500/40"
      >
        <Search size={19} />
        <span className="sr-only">Buscar</span>
      </button>

      {abierto && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 sm:p-4 sm:pt-[10vh] print:hidden" onMouseDown={() => setAbierto(false)}>
          <div
            role="dialog"
            aria-label="Buscar en la app"
            onMouseDown={(e) => e.stopPropagation()}
            className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-auto sm:max-h-[75vh] sm:max-w-2xl sm:rounded-2xl"
          >
            <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
              <Search size={18} className="flex-shrink-0 text-slate-400" />
              <input
                ref={inputRef}
                id="buscador-general"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={alTeclearEnInput}
                placeholder={`Buscar en ${empresaActivaNombre || 'la empresa'}: clientes, facturas, pantallas…`}
                autoComplete="off"
                className="min-w-0 flex-1 bg-transparent text-base text-slate-800 placeholder:text-slate-400 focus:outline-none"
              />
              <button type="button" onClick={() => setAbierto(false)} title="Cerrar" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <X size={18} />
              </button>
            </div>

            <div ref={listaRef} className="min-h-0 flex-1 overflow-y-auto py-1">
              {q.length === 0 && (
                <p className="px-4 py-6 text-sm text-slate-500">
                  Escribe un nombre, RIF, número de factura, referencia o monto, o el nombre de una pantalla o reporte.
                  Solo busca en {empresaActivaNombre || 'la empresa activa'} y en lo que tu usuario puede ver.
                </p>
              )}
              {q.length > 0 && filas.length === 0 && (
                <p className="px-4 py-6 text-sm text-slate-500">
                  {buscando ? 'Buscando…' : `Nada coincide con "${texto.trim()}" en ${empresaActivaNombre || 'esta empresa'}.`}
                </p>
              )}
              {grupos.map((g) => (
                <div key={g.titulo} className="border-t border-slate-100 py-1 first:border-t-0">
                  <p className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{g.titulo}</p>
                  {g.filas.map((f) => {
                    indice += 1;
                    const i = indice;
                    const sel = i === activo;
                    return (
                      <button
                        key={f.clave}
                        type="button"
                        data-indice={i}
                        onMouseMove={() => setActivo(i)}
                        onClick={() => abrir(f)}
                        className={`flex w-full items-center gap-3 px-4 py-2 text-left ${sel ? 'bg-turquoise-50 shadow-[inset_3px_0_0] shadow-turquoise-500' : ''}`}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-slate-800"><Resaltado texto={f.titulo} q={q} /></span>
                          <span className="block truncate text-xs text-slate-500 tabular-nums"><Resaltado texto={f.detalle} q={q} /></span>
                        </span>
                        {f.externo
                          ? <ExternalLink size={14} className="flex-shrink-0 text-slate-400" />
                          : sel && <CornerDownLeft size={14} className="flex-shrink-0 text-slate-400" />}
                      </button>
                    );
                  })}
                </div>
              ))}
              {buscando && filas.length > 0 && <p className="px-4 py-2 text-xs text-slate-400">Buscando datos…</p>}
            </div>

            <div className="hidden items-center gap-4 border-t border-slate-200 px-4 py-2 text-xs text-slate-500 sm:flex">
              <span><kbd className="rounded border border-slate-200 px-1 font-sans">↑</kbd> <kbd className="rounded border border-slate-200 px-1 font-sans">↓</kbd> moverse</span>
              <span><kbd className="rounded border border-slate-200 px-1 font-sans">Enter</kbd> abrir</span>
              <span><kbd className="rounded border border-slate-200 px-1 font-sans">Esc</kbd> cerrar</span>
              <span className="ml-auto truncate">Solo {empresaActivaNombre || 'la empresa activa'}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
